import { acceptHMRUpdate, defineStore } from 'pinia'

import { type SearchResponse, type Label } from '~/types.ts'

export const useLabels = defineStore('labels', () => {
  const { data: labels, meta } = useFetch<SearchResponse<Label>>('labels/searches', {
    inputs: () => ({ organization_id: [stores.organization.id] }),
    immediate: true,
    pagination: true,
    appendData: true,
    meta: {
      pageSize: 200,
    },
    onFetched: () => {
      if (meta.currentPage < meta.pageCount) {
        meta.currentPage++
      }
    },
  })

  async function create(name: string): Promise<Label> {
    const normalizedName = name.trim()
    const existingLabel = labels.value.find((label) => label.name.toLowerCase() === normalizedName.toLowerCase())
    if (existingLabel) {
      return existingLabel
    }

    const { data } = await api.post('labels', { name, color: '#475569' }, { key: null })
    localUpdate(data)

    return data
  }

  async function update(id: number, data: Partial<Label>): Promise<Label> {
    const { data: updatedLabel } = await api.patch(`labels/${id}`, data)

    return localUpdate(updatedLabel)
  }

  async function remove(id: number): Promise<void> {
    await api.delete(`labels/${id}`)
    labels.value = labels.value.filter((label) => label.id !== id)
  }

  function localUpdate(label: Label): Label {
    const existingLabel = labels.value.find((currentLabel) => currentLabel.id === label.id)
    let updatedLabels = labels.value.slice()

    if (existingLabel && existingLabel.index !== label.index) {
      const oldIndex = existingLabel.index
      const newIndex = label.index

      updatedLabels = updatedLabels.map((currentLabel) => {
        if (currentLabel.id === label.id) {
          return { ...currentLabel, ...label }
        }

        if (newIndex < oldIndex && currentLabel.index >= newIndex && currentLabel.index <= oldIndex - 1) {
          return { ...currentLabel, index: currentLabel.index + 1 }
        }

        if (newIndex > oldIndex && currentLabel.index >= oldIndex + 1 && currentLabel.index <= newIndex) {
          return { ...currentLabel, index: currentLabel.index - 1 }
        }

        return currentLabel
      })
    } else if (existingLabel) {
      updatedLabels = updatedLabels.map((currentLabel) => (currentLabel.id === label.id ? { ...currentLabel, ...label } : currentLabel))
    } else {
      updatedLabels = [...updatedLabels, label]
    }

    updatedLabels.sort((left, right) => left.index - right.index)
    labels.value = updatedLabels

    return labels.value.find((currentLabel) => currentLabel.id === label.id) ?? label
  }

  return { labels, create, update, remove, localUpdate }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLabels, import.meta.hot))
}
